import { useState, useEffect, useMemo, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../lib/api';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Modal } from '../components/ui/Modal';
import { StatusBadge } from '../components/ui/Badge';
import { Plus, Settings as SettingsIcon, Megaphone, Trash2 } from 'lucide-react';
import { formatDateTime } from '../lib/utils';
import { useConfirm } from '../components/ui/ConfirmDialog';

interface Campaign {
  id: number;
  slug: string;
  displayName: string;
  status: string;
  fallbackOfferId: number | null;
  botThreshold: number;
  rulesCount: number;
  createdAt: number;
}

interface TrackerDomain {
  id: number;
  hostname: string;
  status: string;
  isDefault: boolean;
}

export function CampaignsPage() {
  const qc = useQueryClient();
  const confirm = useConfirm();
  const [createOpen, setCreateOpen] = useState(false);
  const [rulesOpen, setRulesOpen] = useState<Campaign | null>(null);

  const list = useQuery({
    queryKey: ['campaigns'],
    queryFn: () => apiClient.get<Campaign[]>('/api/v1/campaigns'),
  });

  const offers = useQuery({
    queryKey: ['offers'],
    queryFn: () => apiClient.get<any[]>('/api/v1/offers'),
  });

  // Resolve the default active tracker domain dynamically. Falls back to a
  // placeholder if no domain is registered yet (admin hasn't completed setup).
  // This replaces a hardcoded `tracker.rjnetwork.site` reference that stayed
  // stale after the user pointed traffic at `trk.nguwelitan.online`.
  const domains = useQuery({
    queryKey: ['tracker-domains-default'],
    queryFn: () => apiClient.get<TrackerDomain[]>('/api/v1/tracker-domains'),
    staleTime: 5 * 60_000,
  });
  const defaultDomain =
    (domains.data ?? []).find((d) => d.isDefault && d.status !== 'disabled')?.hostname
    ?? (domains.data ?? []).find((d) => d.status === 'active')?.hostname
    ?? 'your-tracker-domain.com';

  const create = useMutation({
    mutationFn: (data: { slug: string; displayName: string }) =>
      apiClient.post('/api/v1/campaigns', data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['campaigns'] });
      setCreateOpen(false);
    },
  });

  const archive = useMutation({
    mutationFn: ({ id, hard }: { id: number; hard?: boolean }) =>
      apiClient.delete(`/api/v1/campaigns/${id}${hard ? '?hard=true' : ''}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['campaigns'] }),
  });

  const handleArchiveOrDelete = async (camp: { id: number; displayName: string }) => {
    const result = await confirm({
      title: `Delete ${camp.displayName}?`,
      description: 'This permanently removes the campaign + all routing rules. Past clicks keep their campaign_id but lose the slug.',
      variant: 'danger',
      actions: [{ label: 'Delete', variant: 'danger', result: 'delete', icon: <Trash2 size={14} /> }],
    });
    if (result === 'delete') archive.mutate({ id: camp.id, hard: true });
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="font-display text-2xl font-bold uppercase tracking-widest">Campaigns</h1>
          <p className="text-sm text-text-secondary font-mono">// {list.data?.length ?? 0} deployment units</p>
        </div>
        <Button icon={<Plus size={14} />} onClick={() => setCreateOpen(true)}>
          New Campaign
        </Button>
      </div>

      {/* Hint banner: tell user where the actual usable links live, since
          we removed the per-campaign tracking URL column. Without this,
          someone landing here for the first time would wonder how to get
          a shareable link. */}
      <div className="cyber-panel p-3 border-neon-cyan/30 bg-neon-cyan/5 text-[11px] font-mono text-text-secondary leading-relaxed">
        💡 Tracking URL aktual = <code className="text-neon-cyan">https://{defaultDomain}/&lt;slug&gt;?sub_id=&lt;sub_id&gt;</code>.
        Untuk dapat link siap-share dengan sub_id otomatis, buka{' '}
        <a href="/publishers" className="text-neon-cyan hover:underline">/publishers</a>{' '}→ pilih sub-publisher → klik tombol <strong>Get Link</strong>.
      </div>

      <Card className="p-0">
        <div className="overflow-x-auto">
          <table className="table-cyber">
            <thead>
              <tr>
                <th>Identity</th>
                <th>Rules</th>
                <th>Status</th>
                <th>Created</th>
                <th className="text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {(list.data ?? []).map((c) => (
                <tr key={c.id}>
                  <td>
                    <div className="font-display font-bold text-sm">{c.displayName}</div>
                    <div className="text-[11px] text-text-tertiary font-mono">slug: {c.slug}</div>
                  </td>
                  <td className="font-mono text-xs">
                    <button
                      onClick={() => setRulesOpen(c)}
                      className="text-neon-cyan hover:underline"
                    >
                      {c.rulesCount} rule{c.rulesCount !== 1 ? 's' : ''}
                    </button>
                  </td>
                  <td>
                    <StatusBadge status={c.status} />
                  </td>
                  <td className="text-xs text-text-tertiary font-mono">{formatDateTime(c.createdAt)}</td>
                  <td className="text-right">
                    <Button variant="ghost" size="sm" icon={<SettingsIcon size={12} />} onClick={() => setRulesOpen(c)}>
                      Rules
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      icon={<Trash2 size={12} />}
                      onClick={() => handleArchiveOrDelete(c)}
                      title="Archive or delete"
                    />
                  </td>
                </tr>
              ))}
              {(list.data ?? []).length === 0 && !list.isLoading && (
                <tr>
                  <td colSpan={5} className="text-center py-12 text-text-tertiary">
                    <Megaphone className="mx-auto mb-2" size={32} />
                    <p className="font-mono text-xs">no campaigns yet — create one to start tracking</p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Create Modal */}
      <CreateCampaignModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onSubmit={(data) => create.mutate(data)}
        loading={create.isPending}
        defaultDomain={defaultDomain}
      />

      {/* Rules Modal */}
      {rulesOpen && (
        <RulesModal campaign={rulesOpen} onClose={() => setRulesOpen(null)} offers={offers.data ?? []} />
      )}
    </div>
  );
}

function CreateCampaignModal({
  open,
  onClose,
  onSubmit,
  loading,
  defaultDomain,
}: {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: { slug: string; displayName: string }) => void;
  loading: boolean;
  defaultDomain: string;
}) {
  const [slug, setSlug] = useState('');
  const [name, setName] = useState('');
  return (
    <Modal open={open} onClose={onClose} title="New Tracking Unit" description="Define your campaign identity">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          onSubmit({ slug, displayName: name });
        }}
        className="space-y-4"
      >
        <Input
          label="Campaign Slug"
          value={slug}
          onChange={(e) => setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, ''))}
          placeholder="my-campaign"
          hint={`Forms your tracking URL: ${defaultDomain}/{slug}`}
          required
        />
        <Input
          label="Display Name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g. Black Friday Promo"
          required
        />
        <div className="flex gap-2 justify-end pt-2">
          <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
          <Button type="submit" loading={loading} icon={<Plus size={14} />}>Initialize Unit</Button>
        </div>
      </form>
    </Modal>
  );
}

function RulesModal({
  campaign,
  onClose,
  offers,
}: {
  campaign: Campaign;
  onClose: () => void;
  offers: any[];
}) {
  const qc = useQueryClient();

  // ─── Segment-builder state ─────────────────────────────────────────────
  // The form represents ONE segment: a tuple of (countries, device, os) that
  // share the same offer split. Submit creates N × M rules in the DB
  // (N = countries, M = offers in the split).
  //
  // Empty `countries` means catch-all (country='*'). Otherwise each country
  // becomes its own segment row in the routing matrix.
  const [countries, setCountries] = useState<string[]>([]); // ISO-2 codes; [] = catch-all
  const [countryInput, setCountryInput] = useState('');
  const [device, setDevice] = useState('*');
  const [splits, setSplits] = useState<Array<{ offerId: number; weight: number }>>(() =>
    offers.length >= 2
      ? [
          { offerId: offers[0]!.id, weight: 50 },
          { offerId: offers[1]!.id, weight: 50 },
        ]
      : offers.length === 1
      ? [{ offerId: offers[0]!.id, weight: 100 }]
      : [],
  );

  const rules = useQuery({
    queryKey: ['rules', campaign.id],
    queryFn: () => apiClient.get<any[]>(`/api/v1/campaigns/${campaign.id}/rules`),
  });

  const delRule = useMutation({
    mutationFn: (ruleId: number) => apiClient.delete(`/api/v1/campaigns/${campaign.id}/rules/${ruleId}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['rules', campaign.id] });
      qc.invalidateQueries({ queryKey: ['campaigns'] });
    },
  });

  const rulesData = rules.data ?? [];
  const activeRules = rulesData.filter((r) => r.isActive !== false);
  const distributionColors = [
    'bg-neon-cyan',
    'bg-neon-magenta',
    'bg-neon-yellow',
    'bg-neon-green',
    'bg-neon-purple',
    'bg-neon-orange',
  ];

  // ─── Group rules into segments ─────────────────────────────────────────
  // Rules with the SAME filter tuple (country × device × os × source) compete
  // in the same weighted random pool. Rules with DIFFERENT filter tuples are
  // independent segments — they don't share traffic, they each handle a
  // disjoint slice of incoming clicks.
  //
  // The old visual showed all rules in one bar, which was misleading: rule
  // "AU only weight=50" doesn't actually compete with "* weight=50" because
  // they cover different geographies. Grouping by tuple fixes this.
  const isWildcard = (v: string | null | undefined) => {
    const s = (v ?? '').toString().trim().toUpperCase();
    return !s || s === '*' || s === 'GLOBAL' || s === 'ALL' || s === 'ANY';
  };
  const segmentKey = (r: any) =>
    `${(r.country ?? '*').trim().toUpperCase()}|${(r.deviceType ?? '*').trim()}|${(r.os ?? '*').trim()}|${(r.source ?? '*').trim()}`;
  const segmentLabel = (r: any) => {
    const parts: string[] = [];
    if (!isWildcard(r.country)) parts.push(`country=${(r.country as string).toUpperCase()}`);
    if (!isWildcard(r.deviceType)) parts.push(`device=${r.deviceType}`);
    if (!isWildcard(r.os)) parts.push(`os=${r.os}`);
    if (!isWildcard(r.source)) parts.push(`source=${r.source}`);
    return parts.length === 0 ? 'Catch-all (any visitor)' : parts.join(' · ');
  };
  const specificity = (r: any) =>
    (isWildcard(r.country) ? 0 : 1) +
    (isWildcard(r.deviceType) ? 0 : 1) +
    (isWildcard(r.os) ? 0 : 1) +
    (isWildcard(r.source) ? 0 : 1);

  // Group → list of rules that share the same filter tuple
  const segmentMap = new Map<string, any[]>();
  for (const r of activeRules) {
    const k = segmentKey(r);
    if (!segmentMap.has(k)) segmentMap.set(k, []);
    segmentMap.get(k)!.push(r);
  }
  // Sort segments: more specific (overrides) first, then by priority
  const segments = [...segmentMap.entries()]
    .map(([key, rs]) => ({
      key,
      rules: rs,
      label: segmentLabel(rs[0]),
      specificity: specificity(rs[0]),
      priority: Math.min(...rs.map((r) => r.priority ?? 100)),
      totalWeight: rs.reduce((s, r) => s + Math.max(0, r.weight ?? 0), 0) || 1,
    }))
    .sort((a, b) =>
      a.priority !== b.priority ? a.priority - b.priority : b.specificity - a.specificity,
    );

  // Apply preset templates: bulk-create rules in one click. Replaces existing rules.
  async function applyTemplate(kind: 'single' | 'split-2' | 'split-3') {
    if (offers.length === 0) return;
    const ok = await new Promise<boolean>((resolve) => {
      // Simple browser confirm fallback if we don't want dependency on confirm hook here
      resolve(window.confirm('Replace all existing rules with this template?'));
    });
    if (!ok) return;
    // Delete all existing
    await Promise.all(rulesData.map((r) => apiClient.delete(`/api/v1/campaigns/${campaign.id}/rules/${r.id}`).catch(() => {})));
    // Add new rules per template
    const create = (offerId: number, weight: number) =>
      apiClient.post(`/api/v1/campaigns/${campaign.id}/rules`, {
        offerId,
        country: '*',
        deviceType: '*',
        os: '*',
        source: '*',
        weight,
        priority: 100,
      });
    if (kind === 'single') {
      await create(offers[0]!.id, 100);
    } else if (kind === 'split-2' && offers.length >= 2) {
      await create(offers[0]!.id, 50);
      await create(offers[1]!.id, 50);
    } else if (kind === 'split-3' && offers.length >= 3) {
      await create(offers[0]!.id, 34);
      await create(offers[1]!.id, 33);
      await create(offers[2]!.id, 33);
    }
    qc.invalidateQueries({ queryKey: ['rules', campaign.id] });
    qc.invalidateQueries({ queryKey: ['campaigns'] });
  }

  return (
    <Modal open onClose={onClose} title={`Routing Rules: ${campaign.displayName}`} size="xl">
      <div className="space-y-5">
        {/* Quick start templates */}
        <div className="px-3 py-2 border border-neon-cyan/30 bg-neon-cyan/5 rounded text-[11px] font-mono text-text-secondary leading-relaxed">
          <p className="mb-2">
            <strong className="text-neon-cyan">Bagaimana routing bekerja:</strong> setiap klik dievaluasi terhadap rules.
            Rule dengan priority terendah dievaluasi dulu. Di antara rules dengan priority sama, traffic dibagi sesuai bobot (weight).
          </p>
          {offers.length >= 2 && (
            <div className="flex flex-wrap gap-2 pt-2 border-t border-border-subtle">
              <span className="text-text-tertiary text-[10px] uppercase tracking-wider mr-1">Quick start:</span>
              <button
                type="button"
                onClick={() => applyTemplate('single')}
                className="text-[10px] font-display uppercase tracking-wider text-neon-cyan border border-neon-cyan/40 px-2 py-1 rounded hover:bg-neon-cyan/10"
              >
                100% to {offers[0]?.name}
              </button>
              <button
                type="button"
                onClick={() => applyTemplate('split-2')}
                className="text-[10px] font-display uppercase tracking-wider text-neon-magenta border border-neon-magenta/40 px-2 py-1 rounded hover:bg-neon-magenta/10"
              >
                50/50 split · {offers[0]?.name} + {offers[1]?.name}
              </button>
              {offers.length >= 3 && (
                <button
                  type="button"
                  onClick={() => applyTemplate('split-3')}
                  className="text-[10px] font-display uppercase tracking-wider text-neon-yellow border border-neon-yellow/40 px-2 py-1 rounded hover:bg-neon-yellow/10"
                >
                  3-way A/B/C test
                </button>
              )}
            </div>
          )}
        </div>

        {/* Per-segment routing view */}
        {segments.length > 0 ? (
          <div className="space-y-3">
            <div className="text-[10px] font-display uppercase tracking-widest text-text-secondary flex items-center justify-between">
              <span>Traffic Routing · {segments.length} segment{segments.length !== 1 ? 's' : ''}</span>
              <span className="text-text-tertiary normal-case font-mono">
                Most specific segment wins
              </span>
            </div>

            {segments.map((seg, segIdx) => (
              <div
                key={seg.key}
                className="cyber-panel p-3"
              >
                {/* Segment header — what traffic this segment matches */}
                <div className="flex items-start justify-between mb-2 gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="text-[10px] font-mono text-text-tertiary uppercase tracking-wider mb-0.5">
                      Segment {segIdx + 1} · matches when
                    </div>
                    <div className="text-xs font-mono text-neon-cyan break-words">
                      {seg.label}
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-1 shrink-0">
                    <span className="text-[10px] font-mono text-text-tertiary" title="Lower = evaluated first">
                      priority {seg.priority}
                    </span>
                    {seg.specificity > 0 && (
                      <span className="text-[10px] font-mono text-neon-yellow" title="Overrides less-specific rules">
                        specific (+{seg.specificity})
                      </span>
                    )}
                  </div>
                </div>

                {/* Bar showing weighted split inside this segment */}
                <div className="flex h-6 rounded overflow-hidden border border-border-subtle/50">
                  {seg.rules.map((r, i) => {
                    const pct = (Math.max(0, r.weight ?? 0) / seg.totalWeight) * 100;
                    const offer = offers.find((o) => o.id === r.offerId);
                    return (
                      <div
                        key={r.id}
                        className={`${distributionColors[i % distributionColors.length]} flex items-center justify-center text-[10px] font-mono font-bold text-bg-primary px-1`}
                        style={{ width: `${pct}%` }}
                        title={`${offer?.name ?? `#${r.offerId}`} — ${pct.toFixed(0)}%`}
                      >
                        {pct >= 12 && `${pct.toFixed(0)}%`}
                      </div>
                    );
                  })}
                </div>

                {/* Per-rule legend with delete button */}
                <div className="flex flex-wrap gap-2 mt-2">
                  {seg.rules.map((r, i) => {
                    const offer = offers.find((o) => o.id === r.offerId);
                    const pct = (Math.max(0, r.weight ?? 0) / seg.totalWeight) * 100;
                    return (
                      <div
                        key={r.id}
                        className="flex items-center gap-1.5 text-[10px] font-mono px-2 py-1 bg-bg-tertiary rounded border border-border-subtle"
                      >
                        <span className={`w-2 h-2 rounded-sm ${distributionColors[i % distributionColors.length]}`} />
                        <span className="text-text-primary">{offer?.name ?? `#${r.offerId}`}</span>
                        <span className="text-text-tertiary">·</span>
                        <span className="text-neon-cyan font-bold">{pct.toFixed(0)}%</span>
                        <span className="text-text-tertiary">(weight {r.weight})</span>
                        <button
                          onClick={() => delRule.mutate(r.id)}
                          className="text-neon-red hover:opacity-70 ml-1"
                          title="Delete rule"
                        >
                          <Trash2 size={10} />
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}

            {/* Hint about how routing decision is made */}
            <p className="text-[10px] font-mono text-text-tertiary leading-relaxed px-1">
              <strong className="text-text-secondary">How traffic flows:</strong> for each click, system checks
              segments from most-specific to most-generic. First matching segment wins;
              within that segment, traffic splits by weight. If no segment matches → fallback offer (or 404).
            </p>
          </div>
        ) : (
          <div className="cyber-panel p-6 text-center text-text-tertiary font-mono text-xs">
            No rules yet — traffic will fall back to fallback_offer (or 404 if none set)
          </div>
        )}

        {/* Hidden table fallback (kept for screen-reader / dense-info needs) */}
        <details className="cyber-panel">
          <summary className="px-3 py-2 cursor-pointer text-[10px] font-display uppercase tracking-wider text-text-secondary hover:text-text-primary">
            Show raw rules table ({activeRules.length})
          </summary>
          <div className="overflow-x-auto">
            <table className="table-cyber">
              <thead>
                <tr>
                  <th>Offer</th>
                  <th>Country</th>
                  <th>Device</th>
                  <th className="text-right">Weight</th>
                  <th className="text-right">Priority</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {activeRules.map((r) => {
                  const offer = offers.find((o) => o.id === r.offerId);
                  return (
                    <tr key={r.id}>
                      <td className="font-mono text-xs text-neon-cyan">
                        {offer?.name ?? `Offer #${r.offerId}`}
                      </td>
                      <td>
                        <FilterPill value={r.country} kind="country" />
                      </td>
                      <td>
                        <FilterPill value={r.deviceType} kind="device" />
                      </td>
                      <td className="text-right numeric text-text-secondary">{r.weight}</td>
                      <td className="text-right numeric text-text-tertiary">{r.priority}</td>
                      <td className="text-right">
                        <button onClick={() => delRule.mutate(r.id)} className="text-neon-red hover:opacity-70">
                          <Trash2 size={12} />
                        </button>
                      </td>
                    </tr>
                  );
                })}
                {activeRules.length === 0 && (
                  <tr>
                    <td colSpan={6} className="text-center text-text-tertiary py-6 font-mono text-xs">
                      no rules yet
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </details>

        {/* Segment Builder — bulk add rules across multiple countries */}
        <SegmentBuilder
          campaignId={campaign.id}
          offers={offers}
          existingRules={rulesData}
          countries={countries}
          setCountries={setCountries}
          countryInput={countryInput}
          setCountryInput={setCountryInput}
          device={device}
          setDevice={setDevice}
          splits={splits}
          setSplits={setSplits}
          onAdded={() => {
            qc.invalidateQueries({ queryKey: ['rules', campaign.id] });
            qc.invalidateQueries({ queryKey: ['campaigns'] });
          }}
        />
      </div>
    </Modal>
  );
}

// ─── Segment Builder ─────────────────────────────────────────────────────
/**
 * Bulk-create routing rules across multiple countries with the same offer
 * split. One submit can create N (countries) × M (offers in split) rules.
 *
 * Examples:
 *   - countries=[]      , splits=[{Trafee, 100}]               → 1 catch-all rule
 *   - countries=[ID, MY], splits=[{Trafee, 50}, {Torazzo, 50}] → 4 rules total
 *   - countries=[DE]    , splits=[{Trafee, 70}, {Torazzo, 30}] → 2 DE-specific rules
 *
 * The form keeps state in the parent so it persists across re-renders of the
 * rules-list (deletion / preset templates won't reset the form).
 */
function SegmentBuilder({
  campaignId,
  offers,
  existingRules,
  countries,
  setCountries,
  countryInput,
  setCountryInput,
  device,
  setDevice,
  splits,
  setSplits,
  onAdded,
}: {
  campaignId: number;
  offers: any[];
  /** All currently-active rules for this campaign — used to compute clashes
   *  when Replace Mode is on. Pass [] if not available; replace logic falls
   *  back gracefully (delete query still runs but matches nothing). */
  existingRules: any[];
  countries: string[];
  setCountries: (v: string[]) => void;
  countryInput: string;
  setCountryInput: (v: string) => void;
  device: string;
  setDevice: (v: string) => void;
  splits: Array<{ offerId: number; weight: number }>;
  setSplits: (v: Array<{ offerId: number; weight: number }>) => void;
  onAdded: () => void;
}) {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Replace Mode default ON. Industry-norm UX expectation: "I configured
  // routing for DE/AU, hitting submit should result in those rules — not
  // those rules + whatever was already there." User can opt out for true
  // additive behaviour (e.g. layering custom override on top of defaults).
  const [replaceMode, setReplaceMode] = useState(true);

  // Derived: total weight + per-row percentage
  const totalWeight = splits.reduce((s, r) => s + Math.max(0, r.weight ?? 0), 0) || 1;

  // Available offer suggestions for "+ Add Offer" dropdown
  const usedOfferIds = new Set(splits.map((s) => s.offerId));
  const availableOffers = offers.filter((o) => !usedOfferIds.has(o.id));

  // Country chip handlers
  function addCountry(input: string) {
    const code = input.trim().toUpperCase();
    if (!code) return;
    // Allow comma-separated bulk: "ID,MY,SG"
    const codes = code
      .split(/[\s,]+/)
      .map((c) => c.trim())
      .filter((c) => /^[A-Z]{2}$/.test(c) || c === '*' || c === 'GLOBAL' || c === 'ALL');
    const next = [...countries];
    for (const c of codes) {
      const norm = c === '*' || c === 'GLOBAL' || c === 'ALL' ? '*' : c;
      if (!next.includes(norm)) next.push(norm);
    }
    setCountries(next);
    setCountryInput('');
  }
  function removeCountry(c: string) {
    setCountries(countries.filter((x) => x !== c));
  }

  // Split handlers
  function setSplit(idx: number, patch: Partial<{ offerId: number; weight: number }>) {
    setSplits(splits.map((s, i) => (i === idx ? { ...s, ...patch } : s)));
  }
  /** Redistribute weights across an arbitrary list to sum exactly 100.
   *  Floor-divide gives the per-row share, the remainder lands on the first
   *  row to keep the math exact (e.g. 3 offers → [34, 33, 33]). */
  function redistributeEvenly(list: Array<{ offerId: number; weight: number }>) {
    if (list.length === 0) return list;
    const each = Math.floor(100 / list.length);
    const remainder = 100 - each * list.length;
    return list.map((s, i) => ({ ...s, weight: each + (i === 0 ? remainder : 0) }));
  }
  function removeSplit(idx: number) {
    // Auto-redistribute remaining offers to sum 100 — matches the user
    // expectation that "split traffic" always means a 100% pie.
    setSplits(redistributeEvenly(splits.filter((_, i) => i !== idx)));
  }
  function addSplit() {
    if (availableOffers.length === 0) return;
    // BUGFIX: previously defaulted weight to 50 if no remaining capacity, so
    // adding a 3rd offer to a 50/50 split produced 50/50/50 = 150% total.
    // Always redistribute evenly when count changes — user can manually
    // override individual weights afterwards if they want a non-even split.
    const next = [...splits, { offerId: availableOffers[0]!.id, weight: 0 }];
    setSplits(redistributeEvenly(next));
  }
  function distribute() {
    setSplits(redistributeEvenly(splits));
  }

  // ── Clash computation for Replace Mode ────────────────────────────────
  // A rule "clashes" with this submission if its (country, device) tuple
  // matches any (country × device) pair we're about to create. Replace Mode
  // deletes those clashing rules before creating new ones.
  // OS/source are wildcard in this builder, so we don't include them in the key.
  const targetCountriesForClash = countries.length === 0 ? ['*'] : countries;
  const clashingRules = existingRules.filter((r: any) => {
    const ruleCountry = (r.country ?? '*').toString().toUpperCase();
    const ruleDevice = (r.deviceType ?? '*').toString();
    const targetMatchCountry = targetCountriesForClash
      .map((c) => c.toUpperCase())
      .includes(ruleCountry);
    const targetMatchDevice = ruleDevice === device;
    // Only consider rules whose os/source are wildcard — rules with explicit
    // os/source filters are deeper specificity and shouldn't be replaced by
    // this builder which doesn't surface those filters.
    const ruleOsWild = !r.os || r.os === '*';
    const ruleSourceWild = !r.source || r.source === '*';
    return targetMatchCountry && targetMatchDevice && ruleOsWild && ruleSourceWild;
  });

  // How many rules will be created
  const targetCountries = countries.length === 0 ? ['*'] : countries;
  const totalRulesToCreate = targetCountries.length * splits.length;
  const sumWeights = splits.reduce((s, r) => s + (r.weight ?? 0), 0);
  const isValid = splits.length > 0 && splits.every((s) => s.offerId > 0 && s.weight > 0);

  async function submit() {
    if (!isValid) {
      setError('Setiap split harus punya offer dan weight > 0');
      return;
    }
    // Replace Mode: confirm before destructive delete. If user disabled
    // Replace Mode, just append (current behaviour preserved).
    if (replaceMode && clashingRules.length > 0) {
      const ok = window.confirm(
        `Replace Mode: ${clashingRules.length} existing rule(s) for ${targetCountries.join(', ')} ` +
          `(device=${device === '*' ? 'any' : device}) will be deleted before new ones are created. Continue?`,
      );
      if (!ok) return;
    }
    setError(null);
    setSubmitting(true);
    try {
      // Phase 1: delete clashing rules (only if Replace Mode is on).
      // BUGFIX: previously each delete was `.catch(() => {})` which silently
      // ignored 4xx/5xx — partial failure could leave clashing rules behind
      // and then phase 2 created NEW rules on top → silent duplicate weights.
      // Now we use Promise.allSettled and abort phase 2 if any delete failed
      // unrecoverably. A 404 is OK (rule already gone — race with another tab),
      // but auth/server errors are surfaced.
      if (replaceMode && clashingRules.length > 0) {
        const deleteResults = await Promise.allSettled(
          clashingRules.map((r: any) =>
            apiClient.delete(`/api/v1/campaigns/${campaignId}/rules/${r.id}`),
          ),
        );
        const realFailures = deleteResults
          .map((res, i) => ({ res, rule: clashingRules[i] }))
          .filter(({ res }) => {
            if (res.status === 'fulfilled') return false;
            // 404 = rule already gone (concurrent delete from another tab) → OK
            const msg = (res.reason as Error)?.message ?? '';
            return !msg.includes('404') && !msg.toLowerCase().includes('not found');
          });
        if (realFailures.length > 0) {
          throw new Error(
            `Failed to delete ${realFailures.length} clashing rule(s) — aborted to avoid creating duplicates. ` +
              `First error: ${(realFailures[0]!.res as PromiseRejectedResult).reason?.message ?? 'unknown'}`,
          );
        }
      }
      // Phase 2: create new rules — N×M total. allSettled here too so we
      // can surface partial creation failures rather than abort mid-batch.
      const createPairs: Array<{ country: string; sp: { offerId: number; weight: number } }> = [];
      for (const country of targetCountries) {
        for (const sp of splits) createPairs.push({ country, sp });
      }
      const createResults = await Promise.allSettled(
        createPairs.map(({ country, sp }) =>
          apiClient.post(`/api/v1/campaigns/${campaignId}/rules`, {
            offerId: sp.offerId,
            country,
            deviceType: device,
            os: '*',
            source: '*',
            weight: sp.weight,
            priority: 100,
          }),
        ),
      );
      const createFailures = createResults.filter((r) => r.status === 'rejected');
      if (createFailures.length > 0) {
        throw new Error(
          `Created ${createResults.length - createFailures.length} of ${createResults.length} rules — ${createFailures.length} failed. ` +
            `First error: ${((createFailures[0] as PromiseRejectedResult).reason as Error)?.message ?? 'unknown'}`,
        );
      }
      // Reset form after full success
      setCountries([]);
      setCountryInput('');
      onAdded();
    } catch (e) {
      setError((e as Error).message ?? 'Failed to create rules');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="cyber-panel p-4">
      <div className="text-[10px] font-display uppercase tracking-widest text-neon-magenta mb-1">
        + Add Routing Segment
      </div>
      <p className="text-[10px] font-mono text-text-tertiary mb-4 leading-relaxed">
        Tentukan target traffic + cara membaginya antar offer. Submit sekali bikin {' '}
        <span className="text-neon-cyan">{totalRulesToCreate}</span> rule (
        {targetCountries.length} country × {splits.length} offer).
      </p>

      {/* Step 1: target countries (multi-chip) */}
      <div className="mb-4">
        <label className="block text-[10px] font-display uppercase tracking-wider text-text-secondary mb-1.5">
          1️⃣ Apply to country
        </label>
        <div className="flex flex-wrap items-center gap-1.5 px-2 py-2 bg-bg-tertiary border border-border-subtle rounded-sm min-h-[42px]">
          {countries.length === 0 && countryInput.trim() === '' && (
            <span className="text-[10px] font-mono text-text-tertiary italic px-1">
              empty = catch-all (any country) · type 2-letter codes like ID, MY, SG and press Enter
            </span>
          )}
          {countries.map((c) => (
            <span
              key={c}
              className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-mono rounded border ${
                c === '*'
                  ? 'border-text-tertiary text-text-tertiary bg-bg-secondary'
                  : 'border-neon-cyan/40 text-neon-cyan bg-neon-cyan/5'
              }`}
            >
              {c === '*' ? 'any country' : c}
              <button
                onClick={() => removeCountry(c)}
                className="text-neon-red hover:opacity-70 ml-1"
                title="Remove"
              >
                ×
              </button>
            </span>
          ))}
          <CountryAutocomplete
            value={countryInput}
            onChange={setCountryInput}
            onSelect={(c) => addCountry(c)}
            onBackspaceEmpty={() => {
              if (countries.length > 0) removeCountry(countries[countries.length - 1]!);
            }}
            placeholder={countries.length === 0 ? 'Type 2-letter code: ID, MY, SG, ...' : '+'}
          />
        </div>
        {/* Quick presets */}
        <div className="flex flex-wrap gap-1.5 mt-1.5">
          <span className="text-[10px] font-mono text-text-tertiary">Quick:</span>
          {[
            { label: 'Catch-all', codes: [] as string[] },
            { label: 'SEA Tier-1', codes: ['ID', 'MY', 'SG', 'TH', 'PH'] },
            { label: 'Tier-1 EN', codes: ['US', 'GB', 'CA', 'AU'] },
            { label: 'EU', codes: ['DE', 'FR', 'IT', 'ES', 'NL'] },
          ].map((preset) => (
            <button
              key={preset.label}
              type="button"
              onClick={() => setCountries(preset.codes)}
              className="text-[10px] font-mono text-text-secondary hover:text-neon-cyan border border-border-subtle hover:border-neon-cyan/40 px-1.5 py-0.5 rounded"
            >
              {preset.label}
            </button>
          ))}
        </div>
      </div>

      {/* Step 2: device filter */}
      <div className="mb-4">
        <label className="block text-[10px] font-display uppercase tracking-wider text-text-secondary mb-1.5">
          2️⃣ Device filter (optional)
        </label>
        <select value={device} onChange={(e) => setDevice(e.target.value)} className="input-cyber max-w-[200px]">
          <option value="*">Any device</option>
          <option value="mobile">Mobile only</option>
          <option value="tablet">Tablet only</option>
          <option value="desktop">Desktop only</option>
        </select>
      </div>

      {/* Step 3: traffic split */}
      <div className="mb-4">
        <div className="flex items-center justify-between mb-1.5">
          <label className="block text-[10px] font-display uppercase tracking-wider text-text-secondary">
            3️⃣ Split traffic between
          </label>
          <button
            type="button"
            onClick={distribute}
            className="text-[9px] font-mono text-neon-cyan hover:underline"
            title="Even split — auto-set weights to sum 100"
          >
            ↻ even split
          </button>
        </div>
        <div className="space-y-1.5">
          {splits.map((s, i) => {
            const pct = (Math.max(0, s.weight ?? 0) / totalWeight) * 100;
            return (
              <div key={i} className="flex items-center gap-2">
                <select
                  value={s.offerId}
                  onChange={(e) => setSplit(i, { offerId: Number(e.target.value) })}
                  className="input-cyber flex-1 min-w-[140px]"
                >
                  {offers.map((o) => (
                    <option key={o.id} value={o.id}>
                      {o.name}
                    </option>
                  ))}
                </select>
                <div className="relative w-24">
                  <input
                    type="number"
                    value={s.weight}
                    onChange={(e) => setSplit(i, { weight: Number(e.target.value) })}
                    className="input-cyber pr-6 text-right"
                    min={0}
                    max={100}
                  />
                  <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] font-mono text-text-tertiary pointer-events-none">
                    %
                  </span>
                </div>
                <div className="flex-1 max-w-[120px] h-2 bg-bg-tertiary rounded overflow-hidden">
                  <div
                    className="h-full bg-neon-cyan transition-all"
                    style={{ width: `${pct}%` }}
                  />
                </div>
                <button
                  type="button"
                  onClick={() => removeSplit(i)}
                  className="text-neon-red hover:opacity-70 disabled:opacity-30"
                  disabled={splits.length <= 1}
                  title="Remove"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            );
          })}
          {availableOffers.length > 0 && (
            <button
              type="button"
              onClick={addSplit}
              className="text-[10px] font-mono text-neon-magenta hover:underline pt-1"
            >
              + Add another offer to split
            </button>
          )}
        </div>
        <p
          className={`text-[10px] font-mono mt-2 ${
            sumWeights === 100
              ? 'text-neon-green'
              : sumWeights > 0
              ? 'text-neon-yellow'
              : 'text-neon-red'
          }`}
        >
          Total: {sumWeights}%{' '}
          {sumWeights === 100
            ? '✓ exact split'
            : sumWeights === 0
            ? '— set weights'
            : `(weight bekerja relatif — ${sumWeights}% akan dinormalkan ke 100%)`}
        </p>
      </div>

      {/* Replace Mode toggle. Default ON since it matches user expectation
          ("submit = state I configured", not "submit = additive layer"). */}
      <div className="flex items-start gap-2 pt-3 border-t border-border-subtle">
        <input
          type="checkbox"
          id="replace-mode"
          checked={replaceMode}
          onChange={(e) => setReplaceMode(e.target.checked)}
          className="mt-0.5 accent-neon-magenta"
        />
        <label htmlFor="replace-mode" className="flex-1 cursor-pointer">
          <span className="text-[11px] font-display uppercase tracking-wider text-neon-magenta">
            Replace mode
          </span>
          <p className="text-[10px] font-mono text-text-tertiary leading-relaxed mt-0.5">
            {replaceMode ? (
              <>
                Will delete existing rules for the same (country × device) combo before creating new
                ones.{' '}
                {clashingRules.length > 0 ? (
                  <span className="text-neon-yellow">
                    {clashingRules.length} rule{clashingRules.length === 1 ? '' : 's'} will be replaced.
                  </span>
                ) : (
                  <span className="text-neon-green">No clashing rules — clean add.</span>
                )}
              </>
            ) : (
              <>
                Off → rules will be added on top of existing ones (may create duplicates with stacked weights).
              </>
            )}
          </p>
        </label>
      </div>

      {/* Submit */}
      <div className="flex items-center justify-between pt-3 mt-3 border-t border-border-subtle">
        <p className="text-[10px] font-mono text-text-tertiary">
          {targetCountries.length === 1 && targetCountries[0] === '*'
            ? 'Will create rules for ALL traffic'
            : `Will create rules for ${targetCountries.length} country (${targetCountries.join(', ')})`}
          {device !== '*' && ` · ${device} only`}
        </p>
        <Button
          variant="magenta"
          size="sm"
          icon={<Plus size={12} />}
          onClick={submit}
          loading={submitting}
          disabled={!isValid || offers.length === 0 || splits.length === 0}
        >
          {replaceMode && clashingRules.length > 0
            ? `Replace ${clashingRules.length} → Add ${totalRulesToCreate}`
            : `Add ${totalRulesToCreate} Rule${totalRulesToCreate !== 1 ? 's' : ''}`}
        </Button>
      </div>

      {error && (
        <p className="text-[10px] font-mono text-neon-red mt-2">{error}</p>
      )}
    </div>
  );
}

// ─── Country Autocomplete ────────────────────────────────────────────────
// Full ISO-3166-1 alpha-2 list (249 entries) + "*" wildcard at top.
// Used by the country chip input — fuzzy matched on code or display name.
const COUNTRY_LIST: Array<{ code: string; name: string }> = [
  { code: '*', name: 'Any country (catch-all)' },
  { code: 'AD', name: 'Andorra' },
  { code: 'AE', name: 'United Arab Emirates' },
  { code: 'AF', name: 'Afghanistan' },
  { code: 'AG', name: 'Antigua and Barbuda' },
  { code: 'AI', name: 'Anguilla' },
  { code: 'AL', name: 'Albania' },
  { code: 'AM', name: 'Armenia' },
  { code: 'AO', name: 'Angola' },
  { code: 'AQ', name: 'Antarctica' },
  { code: 'AR', name: 'Argentina' },
  { code: 'AS', name: 'American Samoa' },
  { code: 'AT', name: 'Austria' },
  { code: 'AU', name: 'Australia' },
  { code: 'AW', name: 'Aruba' },
  { code: 'AX', name: 'Åland Islands' },
  { code: 'AZ', name: 'Azerbaijan' },
  { code: 'BA', name: 'Bosnia and Herzegovina' },
  { code: 'BB', name: 'Barbados' },
  { code: 'BD', name: 'Bangladesh' },
  { code: 'BE', name: 'Belgium' },
  { code: 'BF', name: 'Burkina Faso' },
  { code: 'BG', name: 'Bulgaria' },
  { code: 'BH', name: 'Bahrain' },
  { code: 'BI', name: 'Burundi' },
  { code: 'BJ', name: 'Benin' },
  { code: 'BL', name: 'Saint Barthélemy' },
  { code: 'BM', name: 'Bermuda' },
  { code: 'BN', name: 'Brunei' },
  { code: 'BO', name: 'Bolivia' },
  { code: 'BQ', name: 'Caribbean Netherlands' },
  { code: 'BR', name: 'Brazil' },
  { code: 'BS', name: 'Bahamas' },
  { code: 'BT', name: 'Bhutan' },
  { code: 'BV', name: 'Bouvet Island' },
  { code: 'BW', name: 'Botswana' },
  { code: 'BY', name: 'Belarus' },
  { code: 'BZ', name: 'Belize' },
  { code: 'CA', name: 'Canada' },
  { code: 'CC', name: 'Cocos (Keeling) Islands' },
  { code: 'CD', name: 'DR Congo' },
  { code: 'CF', name: 'Central African Republic' },
  { code: 'CG', name: 'Congo' },
  { code: 'CH', name: 'Switzerland' },
  { code: 'CI', name: 'Côte d’Ivoire' },
  { code: 'CK', name: 'Cook Islands' },
  { code: 'CL', name: 'Chile' },
  { code: 'CM', name: 'Cameroon' },
  { code: 'CN', name: 'China' },
  { code: 'CO', name: 'Colombia' },
  { code: 'CR', name: 'Costa Rica' },
  { code: 'CU', name: 'Cuba' },
  { code: 'CV', name: 'Cape Verde' },
  { code: 'CW', name: 'Curaçao' },
  { code: 'CX', name: 'Christmas Island' },
  { code: 'CY', name: 'Cyprus' },
  { code: 'CZ', name: 'Czech Republic' },
  { code: 'DE', name: 'Germany' },
  { code: 'DJ', name: 'Djibouti' },
  { code: 'DK', name: 'Denmark' },
  { code: 'DM', name: 'Dominica' },
  { code: 'DO', name: 'Dominican Republic' },
  { code: 'DZ', name: 'Algeria' },
  { code: 'EC', name: 'Ecuador' },
  { code: 'EE', name: 'Estonia' },
  { code: 'EG', name: 'Egypt' },
  { code: 'EH', name: 'Western Sahara' },
  { code: 'ER', name: 'Eritrea' },
  { code: 'ES', name: 'Spain' },
  { code: 'ET', name: 'Ethiopia' },
  { code: 'FI', name: 'Finland' },
  { code: 'FJ', name: 'Fiji' },
  { code: 'FK', name: 'Falkland Islands' },
  { code: 'FM', name: 'Micronesia' },
  { code: 'FO', name: 'Faroe Islands' },
  { code: 'FR', name: 'France' },
  { code: 'GA', name: 'Gabon' },
  { code: 'GB', name: 'United Kingdom' },
  { code: 'GD', name: 'Grenada' },
  { code: 'GE', name: 'Georgia' },
  { code: 'GF', name: 'French Guiana' },
  { code: 'GG', name: 'Guernsey' },
  { code: 'GH', name: 'Ghana' },
  { code: 'GI', name: 'Gibraltar' },
  { code: 'GL', name: 'Greenland' },
  { code: 'GM', name: 'Gambia' },
  { code: 'GN', name: 'Guinea' },
  { code: 'GP', name: 'Guadeloupe' },
  { code: 'GQ', name: 'Equatorial Guinea' },
  { code: 'GR', name: 'Greece' },
  { code: 'GS', name: 'South Georgia' },
  { code: 'GT', name: 'Guatemala' },
  { code: 'GU', name: 'Guam' },
  { code: 'GW', name: 'Guinea-Bissau' },
  { code: 'GY', name: 'Guyana' },
  { code: 'HK', name: 'Hong Kong' },
  { code: 'HM', name: 'Heard & McDonald Islands' },
  { code: 'HN', name: 'Honduras' },
  { code: 'HR', name: 'Croatia' },
  { code: 'HT', name: 'Haiti' },
  { code: 'HU', name: 'Hungary' },
  { code: 'ID', name: 'Indonesia' },
  { code: 'IE', name: 'Ireland' },
  { code: 'IL', name: 'Israel' },
  { code: 'IM', name: 'Isle of Man' },
  { code: 'IN', name: 'India' },
  { code: 'IO', name: 'British Indian Ocean Territory' },
  { code: 'IQ', name: 'Iraq' },
  { code: 'IR', name: 'Iran' },
  { code: 'IS', name: 'Iceland' },
  { code: 'IT', name: 'Italy' },
  { code: 'JE', name: 'Jersey' },
  { code: 'JM', name: 'Jamaica' },
  { code: 'JO', name: 'Jordan' },
  { code: 'JP', name: 'Japan' },
  { code: 'KE', name: 'Kenya' },
  { code: 'KG', name: 'Kyrgyzstan' },
  { code: 'KH', name: 'Cambodia' },
  { code: 'KI', name: 'Kiribati' },
  { code: 'KM', name: 'Comoros' },
  { code: 'KN', name: 'Saint Kitts and Nevis' },
  { code: 'KP', name: 'North Korea' },
  { code: 'KR', name: 'South Korea' },
  { code: 'KW', name: 'Kuwait' },
  { code: 'KY', name: 'Cayman Islands' },
  { code: 'KZ', name: 'Kazakhstan' },
  { code: 'LA', name: 'Laos' },
  { code: 'LB', name: 'Lebanon' },
  { code: 'LC', name: 'Saint Lucia' },
  { code: 'LI', name: 'Liechtenstein' },
  { code: 'LK', name: 'Sri Lanka' },
  { code: 'LR', name: 'Liberia' },
  { code: 'LS', name: 'Lesotho' },
  { code: 'LT', name: 'Lithuania' },
  { code: 'LU', name: 'Luxembourg' },
  { code: 'LV', name: 'Latvia' },
  { code: 'LY', name: 'Libya' },
  { code: 'MA', name: 'Morocco' },
  { code: 'MC', name: 'Monaco' },
  { code: 'MD', name: 'Moldova' },
  { code: 'ME', name: 'Montenegro' },
  { code: 'MF', name: 'Saint Martin' },
  { code: 'MG', name: 'Madagascar' },
  { code: 'MH', name: 'Marshall Islands' },
  { code: 'MK', name: 'North Macedonia' },
  { code: 'ML', name: 'Mali' },
  { code: 'MM', name: 'Myanmar' },
  { code: 'MN', name: 'Mongolia' },
  { code: 'MO', name: 'Macau' },
  { code: 'MP', name: 'Northern Mariana Islands' },
  { code: 'MQ', name: 'Martinique' },
  { code: 'MR', name: 'Mauritania' },
  { code: 'MS', name: 'Montserrat' },
  { code: 'MT', name: 'Malta' },
  { code: 'MU', name: 'Mauritius' },
  { code: 'MV', name: 'Maldives' },
  { code: 'MW', name: 'Malawi' },
  { code: 'MX', name: 'Mexico' },
  { code: 'MY', name: 'Malaysia' },
  { code: 'MZ', name: 'Mozambique' },
  { code: 'NA', name: 'Namibia' },
  { code: 'NC', name: 'New Caledonia' },
  { code: 'NE', name: 'Niger' },
  { code: 'NF', name: 'Norfolk Island' },
  { code: 'NG', name: 'Nigeria' },
  { code: 'NI', name: 'Nicaragua' },
  { code: 'NL', name: 'Netherlands' },
  { code: 'NO', name: 'Norway' },
  { code: 'NP', name: 'Nepal' },
  { code: 'NR', name: 'Nauru' },
  { code: 'NU', name: 'Niue' },
  { code: 'NZ', name: 'New Zealand' },
  { code: 'OM', name: 'Oman' },
  { code: 'PA', name: 'Panama' },
  { code: 'PE', name: 'Peru' },
  { code: 'PF', name: 'French Polynesia' },
  { code: 'PG', name: 'Papua New Guinea' },
  { code: 'PH', name: 'Philippines' },
  { code: 'PK', name: 'Pakistan' },
  { code: 'PL', name: 'Poland' },
  { code: 'PM', name: 'Saint Pierre & Miquelon' },
  { code: 'PN', name: 'Pitcairn Islands' },
  { code: 'PR', name: 'Puerto Rico' },
  { code: 'PS', name: 'Palestine' },
  { code: 'PT', name: 'Portugal' },
  { code: 'PW', name: 'Palau' },
  { code: 'PY', name: 'Paraguay' },
  { code: 'QA', name: 'Qatar' },
  { code: 'RE', name: 'Réunion' },
  { code: 'RO', name: 'Romania' },
  { code: 'RS', name: 'Serbia' },
  { code: 'RU', name: 'Russia' },
  { code: 'RW', name: 'Rwanda' },
  { code: 'SA', name: 'Saudi Arabia' },
  { code: 'SB', name: 'Solomon Islands' },
  { code: 'SC', name: 'Seychelles' },
  { code: 'SD', name: 'Sudan' },
  { code: 'SE', name: 'Sweden' },
  { code: 'SG', name: 'Singapore' },
  { code: 'SH', name: 'Saint Helena' },
  { code: 'SI', name: 'Slovenia' },
  { code: 'SJ', name: 'Svalbard and Jan Mayen' },
  { code: 'SK', name: 'Slovakia' },
  { code: 'SL', name: 'Sierra Leone' },
  { code: 'SM', name: 'San Marino' },
  { code: 'SN', name: 'Senegal' },
  { code: 'SO', name: 'Somalia' },
  { code: 'SR', name: 'Suriname' },
  { code: 'SS', name: 'South Sudan' },
  { code: 'ST', name: 'Sao Tome and Principe' },
  { code: 'SV', name: 'El Salvador' },
  { code: 'SX', name: 'Sint Maarten' },
  { code: 'SY', name: 'Syria' },
  { code: 'SZ', name: 'Eswatini' },
  { code: 'TC', name: 'Turks and Caicos Islands' },
  { code: 'TD', name: 'Chad' },
  { code: 'TF', name: 'French Southern Territories' },
  { code: 'TG', name: 'Togo' },
  { code: 'TH', name: 'Thailand' },
  { code: 'TJ', name: 'Tajikistan' },
  { code: 'TK', name: 'Tokelau' },
  { code: 'TL', name: 'Timor-Leste' },
  { code: 'TM', name: 'Turkmenistan' },
  { code: 'TN', name: 'Tunisia' },
  { code: 'TO', name: 'Tonga' },
  { code: 'TR', name: 'Turkey' },
  { code: 'TT', name: 'Trinidad and Tobago' },
  { code: 'TV', name: 'Tuvalu' },
  { code: 'TW', name: 'Taiwan' },
  { code: 'TZ', name: 'Tanzania' },
  { code: 'UA', name: 'Ukraine' },
  { code: 'UG', name: 'Uganda' },
  { code: 'UM', name: 'U.S. Outlying Islands' },
  { code: 'US', name: 'United States' },
  { code: 'UY', name: 'Uruguay' },
  { code: 'UZ', name: 'Uzbekistan' },
  { code: 'VA', name: 'Vatican City' },
  { code: 'VC', name: 'Saint Vincent and the Grenadines' },
  { code: 'VE', name: 'Venezuela' },
  { code: 'VG', name: 'British Virgin Islands' },
  { code: 'VI', name: 'U.S. Virgin Islands' },
  { code: 'VN', name: 'Vietnam' },
  { code: 'VU', name: 'Vanuatu' },
  { code: 'WF', name: 'Wallis and Futuna' },
  { code: 'WS', name: 'Samoa' },
  { code: 'XK', name: 'Kosovo' },
  { code: 'YE', name: 'Yemen' },
  { code: 'YT', name: 'Mayotte' },
  { code: 'ZA', name: 'South Africa' },
  { code: 'ZM', name: 'Zambia' },
  { code: 'ZW', name: 'Zimbabwe' },
];

/**
 * Country autocomplete — only renders dropdown when user types ≥1 character.
 * Native HTML <datalist> shows all items on focus which is too noisy for a
 * 50+ item list (user complaint). Custom dropdown gives proper search-only UX
 * matching what people expect from modern multi-select inputs (Linear, Notion).
 */
function CountryAutocomplete({
  value,
  onChange,
  onSelect,
  onBackspaceEmpty,
  placeholder,
}: {
  value: string;
  onChange: (v: string) => void;
  onSelect: (code: string) => void;
  onBackspaceEmpty: () => void;
  placeholder?: string;
}) {
  const [open, setOpen] = useState(false);
  const [highlighted, setHighlighted] = useState(0);
  const wrapperRef = useRef<HTMLDivElement>(null);

  // Filter: hanya show kalau user mulai ketik. Empty = closed.
  const query = value.trim().toLowerCase();
  const matches = useMemo(() => {
    if (!query) return [];
    return COUNTRY_LIST.filter(
      (c) =>
        c.code.toLowerCase().startsWith(query) ||
        c.name.toLowerCase().includes(query),
    ).slice(0, 8); // limit untuk dropdown nggak kepanjangan
  }, [query]);

  // Auto-open ketika user ketik, auto-close kalau kosong
  useEffect(() => {
    setOpen(query.length > 0 && matches.length > 0);
    setHighlighted(0);
  }, [query, matches.length]);

  // Close on outside click
  useEffect(() => {
    function onDocClick(e: MouseEvent) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    if (open) document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  function pickItem(idx: number) {
    const item = matches[idx];
    if (!item) return;
    onSelect(item.code);
    setOpen(false);
  }

  return (
    <div ref={wrapperRef} className="relative flex-1 min-w-[120px]">
      <input
        value={value}
        onChange={(e) => onChange(e.target.value.toUpperCase())}
        onKeyDown={(e) => {
          if (e.key === 'ArrowDown') {
            e.preventDefault();
            setHighlighted((h) => Math.min(h + 1, matches.length - 1));
            if (!open) setOpen(matches.length > 0);
          } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setHighlighted((h) => Math.max(h - 1, 0));
          } else if (e.key === 'Enter') {
            e.preventDefault();
            if (open && matches.length > 0) {
              pickItem(highlighted);
            } else if (value.trim()) {
              // Free-form add (e.g. user typed "ID, MY, SG" comma-separated)
              onSelect(value);
              setOpen(false);
            }
          } else if (e.key === ',') {
            e.preventDefault();
            if (value.trim()) onSelect(value);
            setOpen(false);
          } else if (e.key === 'Backspace' && value === '') {
            onBackspaceEmpty();
          } else if (e.key === 'Escape') {
            setOpen(false);
          }
        }}
        onFocus={() => {
          if (query.length > 0 && matches.length > 0) setOpen(true);
        }}
        onBlur={() => {
          // Defer so click on dropdown item registers first
          setTimeout(() => {
            if (value.trim()) onSelect(value);
            setOpen(false);
          }, 150);
        }}
        className="w-full bg-transparent border-0 outline-none text-xs font-mono text-text-primary"
        placeholder={placeholder}
      />

      {open && (
        <div className="absolute z-50 left-0 top-full mt-1 w-72 max-h-72 overflow-y-auto bg-bg-elevated border border-neon-cyan/40 rounded shadow-lg shadow-neon-cyan/10">
          {matches.map((c, i) => (
            <button
              key={c.code}
              type="button"
              onMouseDown={(e) => e.preventDefault()} // prevent input blur
              onClick={() => pickItem(i)}
              onMouseEnter={() => setHighlighted(i)}
              className={`w-full text-left px-3 py-1.5 text-xs font-mono flex items-center gap-2 ${
                i === highlighted
                  ? 'bg-neon-cyan/15 text-neon-cyan'
                  : 'text-text-secondary hover:bg-bg-tertiary'
              }`}
            >
              <span className="font-bold w-6">{c.code === '*' ? '✱' : c.code}</span>
              <span className="text-text-tertiary truncate">{c.name}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/** Render filter values like "*" / "GLOBAL" as a clearer "Any" pill. */
function FilterPill({ value, kind }: { value: string | null; kind: 'country' | 'device' }) {
  const v = (value ?? '').toString().trim();
  const isAny = !v || v === '*' || v.toUpperCase() === 'GLOBAL' || v.toUpperCase() === 'ALL' || v.toUpperCase() === 'ANY';
  if (isAny) {
    return (
      <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-mono text-text-tertiary border border-border-subtle bg-bg-tertiary">
        any {kind}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-mono text-neon-cyan border border-neon-cyan/40 bg-neon-cyan/5">
      {v}
    </span>
  );
}
